import { z } from "zod";

/**
 * family-payments — the Family Tax Benefit (FTB) cluster.
 *
 * Three calc surfaces over one date-keyed Services Australia ruleset:
 *
 *   1. `ftbEntitlement`     — FTB Part A (two-stage Method-1 taper) + Part B
 *      (primary-earner cliff + secondary-earner taper), incl. the end-of-year
 *      supplements (each gated by the $80,000 ATI cap).
 *   2. `ftbEligibilityGap`  — a "you may be eligible past where you think" check:
 *      the BASE rate of Part A keeps paying well above the max-rate cutoff. Info-
 *      only — frames it as "easy to check", never "you're missing money".
 *   3. `familyDebtRisk`     — the cross-payment reconciliation-debt risk. One
 *      family income estimate drives BOTH FTB and CCS; an under-estimate creates
 *      correlated debts. CCS has a 5% withholding buffer; FTB does not.
 *
 * REGULATORY SHAPE (binding): FTB is a Services Australia payment, NOT a s764A
 * financial product — outside the AFSL perimeter, $0 affiliate, no handoff. Copy
 * is information, not advice: no "save $X", no "you should claim", every output
 * cites Services Australia / DSS, and Services Australia is named as the
 * authoritative finaliser. See copy.ts.
 *
 * DATE-KEYED rates: Services Australia indexes FTB rates and income-test
 * thresholds twice a year (20 March / 20 September), not on the 1 July FY
 * boundary — so the ruleset is keyed by effective date (`rates-YYYY-MM-DD.ts`),
 * mirroring the au13 date-period convention, NOT a single fyXXXX-XX file.
 *
 * Pure functions only: inputs -> rules -> outputs. No I/O, no Date.now(); the
 * applicable rate-period date is always passed in.
 */

/** ATI add-back factor reused from the shared income-tests engine for callers
 * who need to derive the family's adjusted taxable income before calling these
 * surfaces. The figure inputs below already expect ATI / per-earner ATI. */

/* ------------------------------------------------------------------------- */
/* Rate-period metadata + rule data                                          */
/* ------------------------------------------------------------------------- */

/**
 * Provenance for a date-keyed Services Australia rate period. Distinct from the
 * shared `RuleSetMeta` (which is FY-keyed and product-enum-tagged) because FTB
 * indexes on 20 Mar / 20 Sep, not 1 July, and family-payments is a cluster, not
 * one of the AU0x products in the shared `PRODUCTS` enum.
 */
export interface FamilyPaymentsRateMeta {
  /** Cluster tag — not a single AU0x product. */
  product: "FAMILY_PAYMENTS";
  /** Stable registry key for this rate period, e.g. "2025-09-20". */
  periodKey: string;
  /** Human label surfaced in copy/audit. */
  periodLabel: string;
  /** Inclusive ISO start date this rate period takes effect (an indexation date). */
  effectiveFrom: string;
  /** Inclusive ISO end date, or null for the current open-ended period. */
  effectiveTo: string | null;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
}

/**
 * FTB Part A maximum-rate band by child age. Per-fortnight figures (Services
 * Australia FTB Part A payment rates). 2025-26: 0–12 = $227.36, 13–15 (and
 * 16–19 in full-time secondary study) = $295.82.
 */
export interface FtbPartAMaxRates {
  /** Max rate per fortnight, child aged 0–12. */
  age0to12: number;
  /** Max rate per fortnight, child aged 13–15 (and approved 16–19 study). */
  age13to19: number;
}

/** FTB Part A income test (Method 1, two-stage). */
export interface FtbPartAIncomeTest {
  /** Lower income free area: max rate applies at/below this ATI. */
  lowerFreeArea: number;
  /** Reduction above the lower free area, per $1 (e.g. 0.20 = 20c). */
  lowerTaperPerDollar: number;
  /** Higher income free area: the base rate applies at/below this ATI. */
  higherFreeArea: number;
  /** Reduction of the base rate above the higher free area, per $1 (e.g. 0.30). */
  higherTaperPerDollar: number;
  /** Base rate per fortnight per child (the floor the lower taper reduces to). */
  baseRatePerChild: number;
}

/** FTB Part B income test (primary-earner cliff + secondary-earner taper). */
export interface FtbPartBIncomeTest {
  /** Primary-earner ATI limit — a CLIFF: above this, no Part B at all. */
  primaryEarnerLimit: number;
  /** Secondary-earner free area: Part B max applies at/below this. */
  secondaryEarnerFreeArea: number;
  /** Reduction above the secondary free area, per $1 (e.g. 0.20 = 20c). */
  secondaryTaperPerDollar: number;
  /** Part B max rate per fortnight — youngest child under 5. */
  maxRateYoungestUnder5: number;
  /** Part B max rate per fortnight — youngest child 5–18. */
  maxRateYoungest5to18: number;
}

/** End-of-year supplements, each gated by the $80,000 ATI cap (all-or-nothing). */
export interface FtbSupplements {
  /** FTB Part A supplement, per child per year. */
  partAPerChild: number;
  /** FTB Part B supplement, per family per year. */
  partBPerFamily: number;
  /**
   * Combined-ATI cap for BOTH supplements: above this ATI the supplement is nil,
   * with NO taper (a cliff). It does NOT persist above this income.
   */
  supplementIncomeCap: number;
}

/**
 * The CCS automatic withholding buffer — held here for the cross-payment debt-
 * risk surface. CCS withholds a default % of the subsidy at point of payment as
 * overpayment protection; FTB has NO equivalent instalment withholding. The
 * buffer is adjustable by the family (online up to twice/year, more by phone).
 */
export interface CcsWithholding {
  /** Default withholding fraction (e.g. 0.05 = 5%). */
  defaultRate: number;
}

/** A complete family-payments ruleset for one date-keyed indexation period. */
export interface FamilyPaymentsRuleSet extends FamilyPaymentsRateMeta {
  partAMaxRates: FtbPartAMaxRates;
  partAIncomeTest: FtbPartAIncomeTest;
  partBIncomeTest: FtbPartBIncomeTest;
  supplements: FtbSupplements;
  ccsWithholding: CcsWithholding;
  /** Fortnights per year used to annualise per-fortnight rates (Services
   * Australia pays FTB across the year; the standard divisor is 26). */
  fortnightsPerYear: number;
}

/* ------------------------------------------------------------------------- */
/* Surface 1 — ftbEntitlement                                                */
/* ------------------------------------------------------------------------- */

/** A dependent child for FTB Part A — age band drives the per-child max rate. */
export const FtbChildSchema = z.object({
  /** Child's age in years. */
  age: z.number().int().min(0).max(19),
});
export type FtbChild = z.infer<typeof FtbChildSchema>;

export const FtbEntitlementInputSchema = z.object({
  /** Combined family adjusted taxable income (ATI). */
  familyAti: z.number().min(0),
  /** Dependent children claimed for FTB Part A. */
  children: z.array(FtbChildSchema).min(0),
  /**
   * Whether the family qualifies for FTB Part B at all (single parent / sole
   * primary earner / grandparent carer with a child under the age limit). The
   * income test is applied here; non-income eligibility (youngest-child age,
   * residency) is the caller's gate.
   */
  claimPartB: z.boolean().default(false),
  /** Age of the youngest child — selects the Part B max rate band. */
  youngestChildAge: z.number().int().min(0).max(18).optional(),
  /** Higher individual ATI in the couple (the "primary earner"). For a single
   * parent, this equals their ATI. Drives the Part B cliff. */
  primaryEarnerAti: z.number().min(0).default(0),
  /** Lower individual ATI in the couple (the "secondary earner"). 0 for a
   * single-parent family (no secondary-earner test). */
  secondaryEarnerAti: z.number().min(0).default(0),
  /** True for a single-parent family — no secondary-earner test is applied. */
  singleParent: z.boolean().default(false),
});
export type FtbEntitlementInput = z.infer<typeof FtbEntitlementInputSchema>;

export interface FtbPartAResult {
  /** Sum of the per-child MAXIMUM rates per fortnight (before the income test). */
  maxRatePerFortnight: number;
  /** Sum of the per-child BASE rates per fortnight. */
  baseRatePerFortnight: number;
  /** Part A rate per fortnight AFTER the two-stage income test. */
  ratePerFortnight: number;
  /** Annualised Part A (rate × fortnights/yr), excluding the supplement. */
  annualRate: number;
  /** Which taper stage bit: "max" (full), "lower-taper" (20c toward base),
   * "base" (base rate, untouched), "higher-taper" (30c off base), or "nil". */
  taperStage: "max" | "lower-taper" | "base" | "higher-taper" | "nil";
}

export interface FtbPartBResult {
  /** Whether Part B is payable at all after the income test. */
  payable: boolean;
  /** Part B rate per fortnight after the income test. */
  ratePerFortnight: number;
  /** Annualised Part B, excluding the supplement. */
  annualRate: number;
  /** Why Part B is nil, when it is: "over-primary-limit" (cliff),
   * "secondary-taper-out", or null when payable / not claimed. */
  nilReason: "over-primary-limit" | "secondary-taper-out" | null;
}

export interface FtbSupplementResult {
  /** Part A supplement payable (per-child × children), 0 if over the cap. */
  partA: number;
  /** Part B supplement payable (per family), 0 if over the cap. */
  partB: number;
  /** True when family ATI exceeds the $80,000 supplement cap (both nil). */
  overSupplementCap: boolean;
}

export interface FtbEntitlementOutput {
  partA: FtbPartAResult;
  partB: FtbPartBResult;
  supplement: FtbSupplementResult;
  /** Indicative total estimate for the year (Part A + Part B annual + the
   * supplements). An ESTIMATE — Services Australia finalises at balancing. */
  estimatedAnnualTotal: number;
}

/* ------------------------------------------------------------------------- */
/* Surface 2 — ftbEligibilityGap                                             */
/* ------------------------------------------------------------------------- */

export const FtbEligibilityGapInputSchema = z.object({
  /** Combined family adjusted taxable income (ATI). */
  familyAti: z.number().min(0),
  /** Number of dependent children (any age band — the gap mechanic is the
   * base-rate cut-out, which is child-count-sensitive). */
  childCount: z.number().int().min(1),
});
export type FtbEligibilityGapInput = z.infer<typeof FtbEligibilityGapInputSchema>;

export interface FtbEligibilityGapOutput {
  /** True if the family is above the max-rate (lower free area) cutoff. */
  aboveMaxRateCutoff: boolean;
  /** True if the base rate of Part A may STILL be payable (i.e. ATI is below
   * the point where the base rate tapers to nil for this child count). */
  baseRateMayApply: boolean;
  /** True if the end-of-year supplement is NOT available (ATI over the $80k cap).
   * Copy must NOT imply the supplement persists above the cap. */
  supplementUnavailable: boolean;
  /** The lower income free area (max-rate cutoff) for context. */
  maxRateCutoff: number;
  /** The point at which the base rate tapers to nil, for this child count. */
  baseRateCutoff: number;
}

/* ------------------------------------------------------------------------- */
/* Surface 3 — familyDebtRisk                                                */
/* ------------------------------------------------------------------------- */

export const FamilyDebtRiskInputSchema = z.object({
  /** The family income ESTIMATE currently lodged with Services Australia — the
   * one figure that drives BOTH FTB and CCS for the year. */
  estimatedFamilyAti: z.number().min(0),
  /** The family's likely ACTUAL ATI (a better estimate / projection). When this
   * exceeds the lodged estimate, both payments may have been overpaid. */
  projectedActualAti: z.number().min(0),
  /** Indicative FTB paid per fortnight on the lodged estimate (from
   * `ftbEntitlement`). Used to size the FTB-side debt. Optional. */
  ftbPaidPerFortnight: z.number().min(0).default(0),
  /** Indicative CCS subsidy paid per fortnight on the lodged estimate (from the
   * au13 engine). Used to size the CCS-side debt. Optional. */
  ccsPaidPerFortnight: z.number().min(0).default(0),
  /** The CCS withholding fraction the family has actually set (defaults to the
   * ruleset's standard rate). FTB has no equivalent — it is unbuffered. */
  ccsWithholdingRate: z.number().min(0).max(1).optional(),
});
export type FamilyDebtRiskInput = z.infer<typeof FamilyDebtRiskInputSchema>;

export interface FamilyDebtRiskOutput {
  /** True when the projected actual ATI exceeds the lodged estimate (the
   * under-estimate that creates correlated debts). */
  underEstimated: boolean;
  /** How far the estimate is under, in dollars (0 if not under). */
  estimateShortfall: number;
  /**
   * Indicative FTB-side reconciliation exposure for the year. FTB is UNBUFFERED
   * — no withholding offsets it. "At least this much, possibly more." 0 if not
   * under-estimated.
   */
  ftbDebtExposure: number;
  /**
   * Indicative CCS-side reconciliation exposure for the year, AFTER netting the
   * withholding buffer the family has set. The 5% (or adjusted) buffer absorbs
   * part of it; the rest is exposure. 0 if not under-estimated.
   */
  ccsDebtExposure: number;
  /** The CCS buffer (withheld amount) credited against the CCS exposure. */
  ccsBufferCredit: number;
  /** Combined indicative exposure (FTB + net CCS). "At least this much." */
  combinedDebtExposure: number;
  /** The withholding fraction applied to the CCS side. */
  ccsWithholdingRate: number;
}
